import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Columns, AlignJustify, Palette, Type, SlidersHorizontal, Sparkles } from 'lucide-react';

/**
 * FormThemePanel - Theme customization panel for the form builder
 * 
 * Provides theme presets, color controls, typography, spacing, and layout options.
 */

// ============ Theme Presets ============

export const THEME_PRESETS = {
  minimal: {
    preset: 'minimal',
    backgroundColor: '#ffffff',
    formBackgroundColor: '#ffffff',
    primaryColor: '#111827',
    textColor: '#111827',
    labelColor: '#374151',
    inputBorderColor: '#e5e7eb',
    inputBorderRadius: '0',
    buttonBorderRadius: '4',
    buttonTextColor: '#ffffff',
    fontFamily: '',
    headingFontFamily: '',
    fontSize: '16',
    fieldSpacing: '32',
    formWidth: '100',
    formMaxWidth: '672',
    formPadding: '24',
    layout: 'single',
  },
  bold: {
    preset: 'bold',
    backgroundColor: '#7c3aed',
    formBackgroundColor: '#ffffff',
    primaryColor: '#7c3aed',
    textColor: '#111827',
    labelColor: '#111827',
    inputBorderColor: '#d1d5db',
    inputBorderRadius: '12',
    buttonBorderRadius: '12',
    buttonTextColor: '#ffffff',
    fontFamily: 'Space Grotesk',
    headingFontFamily: '',
    fontSize: '17',
    fieldSpacing: '28',
    formWidth: '100',
    formMaxWidth: '672',
    formPadding: '32',
    layout: 'single',
  },
  corporate: {
    preset: 'corporate',
    backgroundColor: '#f8fafc',
    formBackgroundColor: '#ffffff',
    primaryColor: '#1e40af',
    textColor: '#1e293b',
    labelColor: '#334155',
    inputBorderColor: '#cbd5e1',
    inputBorderRadius: '6',
    buttonBorderRadius: '6',
    buttonTextColor: '#ffffff',
    fontFamily: 'Inter',
    headingFontFamily: '',
    fontSize: '15',
    fieldSpacing: '24',
    formWidth: '100',
    formMaxWidth: '672',
    formPadding: '32',
    layout: 'single',
  },
  playful: {
    preset: 'playful',
    backgroundColor: '#fef3c7',
    formBackgroundColor: '#ffffff',
    primaryColor: '#f59e0b',
    textColor: '#292524',
    labelColor: '#44403c',
    inputBorderColor: '#e7e5e4',
    inputBorderRadius: '16',
    buttonBorderRadius: '24',
    buttonTextColor: '#ffffff',
    fontFamily: 'DM Sans',
    headingFontFamily: '',
    fontSize: '16',
    fieldSpacing: '28',
    formWidth: '100',
    formMaxWidth: '672',
    formPadding: '28',
    layout: 'single',
  },
};

export const DEFAULT_THEME = {
  preset: '',
  backgroundColor: '#f9fafb',
  formBackgroundColor: '#ffffff',
  primaryColor: '#111827',
  textColor: '#111827',
  labelColor: '#111827',
  inputBorderColor: '#e5e7eb',
  inputBorderRadius: '0',
  buttonBorderRadius: '8',
  buttonTextColor: '#ffffff',
  fontFamily: '',
  headingFontFamily: '',
  fontSize: '16',
  fieldSpacing: '24',
  formWidth: '100',
  formMaxWidth: '672',
  formPadding: '24',
  layout: 'single',
};

// ============ Font Options ============

const FONT_OPTIONS = [
  { value: '', label: 'System Default' },
  { value: 'Inter', label: 'Inter' },
  { value: 'DM Sans', label: 'DM Sans' },
  { value: 'Space Grotesk', label: 'Space Grotesk' },
  { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Nunito', label: 'Nunito' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Raleway', label: 'Raleway' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Merriweather', label: 'Merriweather' },
  { value: 'Source Serif Pro', label: 'Source Serif Pro' },
  { value: 'Libre Baskerville', label: 'Libre Baskerville' },
];

// ============ Inline Components ============

function ThemeColorInput({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <label className="text-xs text-gray-600 dark:text-gray-400 w-28 flex-shrink-0">
        {label}
      </label>
      <div className="flex items-center gap-1.5 flex-1">
        <div className="relative">
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="w-7 h-7 rounded border border-gray-200 dark:border-gray-600 cursor-pointer p-0 appearance-none"
            style={{ backgroundColor: value || '#000000' }}
          />
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 font-mono"
        />
      </div>
    </div>
  );
}

function ThemeSelect({ label, value, onChange, options }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <label className="text-xs text-gray-600 dark:text-gray-400 w-28 flex-shrink-0">
        {label}
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, children, defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-100 dark:border-gray-800">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />}
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide">
            {title}
          </span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 space-y-1">
          {children}
        </div>
      )}
    </div>
  );
}

// ============ Preset Cards ============

function PresetCard({ name, preset, isActive, onClick }) {
  const presetData = THEME_PRESETS[preset];

  return (
    <button
      onClick={onClick}
      className={`relative p-2.5 rounded-lg border-2 transition-all duration-150 text-left cursor-pointer ${
        isActive
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-950 ring-1 ring-brand-500/30'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
      }`}
    >
      {/* Mini Preview */}
      <div
        className="w-full h-16 rounded-md mb-2 flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: presetData.backgroundColor }}
      >
        <div
          className="w-3/4 rounded p-1.5"
          style={{
            backgroundColor: presetData.formBackgroundColor,
            boxShadow: presetData.formBackgroundColor !== presetData.backgroundColor
              ? '0 1px 3px rgba(0,0,0,0.1)'
              : 'none',
          }}
        >
          {/* Mini form sketch */}
          <div className="space-y-1">
            <div className="h-1 rounded-full w-3/4" style={{ backgroundColor: presetData.labelColor, opacity: 0.3 }} />
            <div
              className="h-2 rounded-sm w-full"
              style={{ border: `1px solid ${presetData.inputBorderColor}`, borderRadius: `${Math.min(parseInt(presetData.inputBorderRadius), 4)}px` }}
            />
            <div className="h-1 rounded-full w-1/2" style={{ backgroundColor: presetData.labelColor, opacity: 0.3 }} />
            <div
              className="h-2 rounded-sm w-full"
              style={{ border: `1px solid ${presetData.inputBorderColor}`, borderRadius: `${Math.min(parseInt(presetData.inputBorderRadius), 4)}px` }}
            />
            <div
              className="h-2.5 rounded-sm w-full mt-1"
              style={{
                backgroundColor: presetData.primaryColor,
                borderRadius: `${Math.min(parseInt(presetData.buttonBorderRadius), 4)}px`,
              }}
            />
          </div>
        </div>
      </div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">{name}</span>
      {isActive && (
        <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-brand-500 rounded-full flex items-center justify-center">
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </button>
  );
}

// ============ Main Component ============

export default function FormThemePanel({ theme, onChange }) {
  // Migrate legacy pixel-based formWidth to percentage + formMaxWidth
  const migratedTheme = { ...theme };
  if (theme?.formWidth && parseInt(theme.formWidth) > 100) {
    migratedTheme.formMaxWidth = theme.formWidth; // e.g. '672' becomes the maxWidth
    migratedTheme.formWidth = '100'; // default to 100%
  }
  const currentTheme = { ...DEFAULT_THEME, ...migratedTheme };

  const updateTheme = (key, value) => {
    const updated = { ...currentTheme, [key]: value, preset: '' };
    onChange(updated);
  };

  const applyPreset = (presetName) => {
    const preset = THEME_PRESETS[presetName];
    if (preset) {
      onChange({ ...preset });
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand-500" />
          Form Theme
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Customize how your form looks to respondents
        </p>
      </div>

      {/* Presets */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide flex items-center gap-1.5">
          Theme Presets
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(THEME_PRESETS).map(([key, preset]) => (
            <PresetCard
              key={key}
              name={key}
              preset={key}
              isActive={currentTheme.preset === key}
              onClick={() => applyPreset(key)}
            />
          ))}
        </div>
      </div>

      {/* Colors */}
      <CollapsibleSection title="Colors" icon={Palette}>
        <ThemeColorInput
          label="Background"
          value={currentTheme.backgroundColor}
          onChange={(v) => updateTheme('backgroundColor', v)}
        />
        <ThemeColorInput
          label="Form Background"
          value={currentTheme.formBackgroundColor}
          onChange={(v) => updateTheme('formBackgroundColor', v)}
        />
        <ThemeColorInput
          label="Primary Color"
          value={currentTheme.primaryColor}
          onChange={(v) => updateTheme('primaryColor', v)}
        />
        <ThemeColorInput
          label="Text Color"
          value={currentTheme.textColor}
          onChange={(v) => updateTheme('textColor', v)}
        />
        <ThemeColorInput
          label="Label Color"
          value={currentTheme.labelColor}
          onChange={(v) => updateTheme('labelColor', v)}
        />
        <ThemeColorInput
          label="Input Border"
          value={currentTheme.inputBorderColor}
          onChange={(v) => updateTheme('inputBorderColor', v)}
        />
        <ThemeColorInput
          label="Button Text"
          value={currentTheme.buttonTextColor}
          onChange={(v) => updateTheme('buttonTextColor', v)}
        />
      </CollapsibleSection>

      {/* Typography */}
      <CollapsibleSection title="Typography" icon={Type}>
        <ThemeSelect
          label="Body Font"
          value={currentTheme.fontFamily}
          onChange={(v) => updateTheme('fontFamily', v)}
          options={FONT_OPTIONS}
        />
        <ThemeSelect
          label="Heading Font"
          value={currentTheme.headingFontFamily}
          onChange={(v) => updateTheme('headingFontFamily', v)}
          options={FONT_OPTIONS}
        />
        <ThemeSelect
          label="Font Size"
          value={currentTheme.fontSize}
          onChange={(v) => updateTheme('fontSize', v)}
          options={[
            { value: '14', label: '14px — Small' },
            { value: '15', label: '15px — Compact' },
            { value: '16', label: '16px — Default' },
            { value: '17', label: '17px — Comfortable' },
            { value: '18', label: '18px — Large' },
            { value: '20', label: '20px — Extra Large' },
          ]}
        />
      </CollapsibleSection>

      {/* Spacing & Layout */}
      <CollapsibleSection title="Spacing & Layout" icon={SlidersHorizontal}>
        <ThemeSelect
          label="Form Width"
          value={currentTheme.formWidth}
          onChange={(v) => updateTheme('formWidth', v)}
          options={[
            { value: '60', label: '60%' },
            { value: '70', label: '70%' },
            { value: '80', label: '80%' },
            { value: '90', label: '90%' },
            { value: '100', label: '100% — Default' },
          ]}
        />
        <ThemeSelect
          label="Form Max-width"
          value={currentTheme.formMaxWidth}
          onChange={(v) => updateTheme('formMaxWidth', v)}
          options={[
            { value: '512', label: '512px — Narrow' },
            { value: '672', label: '672px — Default' },
            { value: '768', label: '768px — Wide' },
            { value: '896', label: '896px — Extra Wide' },
            { value: '1024', label: '1024px — Full' },
            { value: 'none', label: 'None' },
          ]}
        />
        <ThemeSelect
          label="Form Padding"
          value={currentTheme.formPadding}
          onChange={(v) => updateTheme('formPadding', v)}
          options={[
            { value: '16', label: '16px — Tight' },
            { value: '20', label: '20px — Compact' },
            { value: '24', label: '24px — Default' },
            { value: '28', label: '28px — Comfortable' },
            { value: '32', label: '32px — Spacious' },
            { value: '40', label: '40px — Generous' },
          ]}
        />
        <ThemeSelect
          label="Field Spacing"
          value={currentTheme.fieldSpacing}
          onChange={(v) => updateTheme('fieldSpacing', v)}
          options={[
            { value: '16', label: '16px — Tight' },
            { value: '20', label: '20px — Compact' },
            { value: '24', label: '24px — Default' },
            { value: '28', label: '28px — Comfortable' },
            { value: '32', label: '32px — Spacious' },
            { value: '40', label: '40px — Generous' },
          ]}
        />
        <ThemeSelect
          label="Input Radius"
          value={currentTheme.inputBorderRadius}
          onChange={(v) => updateTheme('inputBorderRadius', v)}
          options={[
            { value: '0', label: '0px — Sharp (underline)' },
            { value: '4', label: '4px — Subtle' },
            { value: '6', label: '6px — Default' },
            { value: '8', label: '8px — Rounded' },
            { value: '12', label: '12px — More Rounded' },
            { value: '16', label: '16px — Pill-ish' },
            { value: '24', label: '24px — Full Pill' },
          ]}
        />
        <ThemeSelect
          label="Button Radius"
          value={currentTheme.buttonBorderRadius}
          onChange={(v) => updateTheme('buttonBorderRadius', v)}
          options={[
            { value: '0', label: '0px — Sharp' },
            { value: '4', label: '4px — Subtle' },
            { value: '6', label: '6px — Default' },
            { value: '8', label: '8px — Rounded' },
            { value: '12', label: '12px — More Rounded' },
            { value: '16', label: '16px — Pill-ish' },
            { value: '24', label: '24px — Full Pill' },
          ]}
        />

        {/* Layout Toggle */}
        <div className="flex items-center gap-2 py-1 mt-1">
          <label className="text-xs text-gray-600 dark:text-gray-400 w-28 flex-shrink-0">
            Layout
          </label>
          <div className="flex rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => updateTheme('layout', 'single')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors ${
                currentTheme.layout === 'single'
                  ? 'bg-brand-500 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <AlignJustify className="w-3 h-3" />
              Single
            </button>
            <button
              onClick={() => updateTheme('layout', 'two-column')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors border-l border-gray-200 dark:border-gray-600 ${
                currentTheme.layout === 'two-column'
                  ? 'bg-brand-500 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Columns className="w-3 h-3" />
              Two Column
            </button>
          </div>
        </div>
      </CollapsibleSection>

      {/* Reset */}
      <div className="px-4 py-3">
        <button
          onClick={() => onChange({ ...DEFAULT_THEME })}
          className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-2 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Reset to Default
        </button>
      </div>
    </div>
  );
}
